import { useQuery, useReactiveVar } from '@apollo/client';
import { CircularProgress, MenuItem, Select, type SelectChangeEvent } from '@mui/material';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { get } from 'lodash';
import type React from 'react';
import { useEffect } from 'react';
import { allViewsMin } from 'src/api/viewModels/allViews';
import styles from 'src/HomePage/DrawerAndPushedContents/PushedContents/TopPaginationNavBar/SortBy/SortByStyles';
import { cleanPayload } from 'src/helpers/common';
import viewStateVar from 'src/store/viewState';
import type { BaseSearchParams } from 'src/utils/searchSchemas';
import DownwardUpwardArrows from '../DownwardUpwardArrows/DownwardUpwardArrows';

interface FieldNode {
  id: string;
  slug: string;
  name?: string;
}

interface ViewFieldNode {
  id: string;
  fieldLabel: string;
  sequence: number;
  field: FieldNode;
  fieldType?: string;
}

interface ViewFieldEdge {
  node: ViewFieldNode;
}

interface ViewFieldSet {
  edges: ViewFieldEdge[];
}

interface ViewModel {
  viewfieldSet: ViewFieldSet;
}

interface SortByValue {
  value: string;
  fieldType: string;
}

type SortDirection = 'asc' | 'desc';

export default function SortBy(): React.ReactElement {
  const classes = styles();
  const navigate = useNavigate();
  // Get typed search params from TanStack Router - this is the source of truth
  const search = useSearch({ strict: false }) as BaseSearchParams;
  const { sortData } = useReactiveVar(viewStateVar);
  const { data, loading, error } = useQuery(allViewsMin, {
    variables: {
      viewName: 'Library',
      componentName: 'MediaSortByMenu',
    },
    fetchPolicy: 'cache-and-network',
  });

  const viewModel = cleanPayload(data)[0] as ViewModel | undefined;

  // Parse sort from URL search params
  const [currentSortSlug, currentSortOrder] = (search.sort || '').split(':') as [
    string | undefined,
    SortDirection | undefined,
  ];

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally only depends on data to avoid overwriting user changes
  useEffect(() => {
    if (!viewModel?.viewfieldSet) return;
    const hasStateSort = !!viewStateVar().sortData?.sortBy?.value;

    // Initialize state if not set
    if (!hasStateSort) {
      initSortByParams(viewModel.viewfieldSet);
    }

    // If URL lacks sort, seed it with default (desc)
    if (!search.sort) {
      const [defaultEdge, orderBy] = getDefaultValue(viewModel.viewfieldSet);
      const slug = defaultEdge?.node?.field?.slug;
      if (slug) {
        navigate({
          search: ((prev: Record<string, unknown>) => ({
            ...prev,
            sort: `${slug}:${orderBy}`,
            // biome-ignore lint/suspicious/noExplicitAny: TanStack Router navigate requires type cast for dynamic search params
          })) as any,
        });
      }
    }
    // NOTE: Do not depend on sortData here to avoid overwriting user changes
  }, [data]);

  const initSortByParams = (viewfieldSet: ViewFieldSet): void => {
    const [defaultEdge, orderBy] = getDefaultValue(viewfieldSet);

    // Reflect default into global state
    viewStateVar({
      ...viewStateVar(),
      sortData: {
        field: defaultEdge ?? null,
        sortBy: massageValue(defaultEdge),
        orderBy,
      },
    });
  };

  const getDefaultValue = (viewfieldSet: ViewFieldSet): [ViewFieldEdge | null | undefined, SortDirection] => {
    // Use typed search params
    if (search.sort) {
      const [val, order] = search.sort.split(':');
      const filtered = viewfieldSet.edges.filter((a) => a.node.field.slug === val);
      if (filtered.length > 0) {
        return [filtered[0], (order as SortDirection) || 'desc'];
      }
    }

    return [viewfieldSet.edges[0], 'desc'];
  };

  const massageValue = (value: ViewFieldEdge | null | undefined): SortByValue => ({
    value: get(value, 'node.field.slug', '') as string,
    fieldType: get(value, 'node.fieldType', '') as string,
  });

  const onChange = (e: SelectChangeEvent<string>): void => {
    const slug = e.target.value;
    const viewfieldSet = (cleanPayload(data)[0] as ViewModel | undefined)?.viewfieldSet;
    const edge = viewfieldSet?.edges.find((a) => a.node.field.slug === slug);
    const sortBy = massageValue(edge);

    // Get current order from URL or state
    const order = currentSortOrder || sortData.orderBy || 'desc';

    // Update global state (for UI display)
    viewStateVar({
      ...viewStateVar(),
      sortData: { ...viewStateVar().sortData, sortBy, field: edge ?? null },
    });

    // Update URL via TanStack Router navigate
    navigate({
      search: ((prev: Record<string, unknown>) => ({
        ...prev,
        sort: `${slug}:${order}`,
        page: 1,
        // biome-ignore lint/suspicious/noExplicitAny: TanStack Router navigate requires type cast for dynamic search params
      })) as any,
    });
  };

  const onArrowClick = (orderBy: SortDirection): void => {
    // Determine current slug robustly: state -> URL -> view model default
    let slug: string | undefined = sortData?.sortBy?.value;
    if (!slug && currentSortSlug) {
      slug = currentSortSlug;
    }
    if (!slug) {
      const edges = (cleanPayload(data)[0] as ViewModel | undefined)?.viewfieldSet?.edges;
      slug = edges?.[0]?.node?.field?.slug;
    }
    if (!slug) return; // nothing to do until options load

    // Update global state (for UI display)
    viewStateVar({
      ...viewStateVar(),
      sortData: { ...viewStateVar().sortData, orderBy },
    });

    // Update URL via TanStack Router navigate
    navigate({
      search: ((prev: Record<string, unknown>) => ({
        ...prev,
        sort: `${slug}:${orderBy}`,
        page: 1,
        // biome-ignore lint/suspicious/noExplicitAny: TanStack Router navigate requires type cast for dynamic search params
      })) as any,
    });
  };

  if (loading) return <CircularProgress color="secondary" />;
  if (error) return <div>`Error! ${error.message}`</div>;

  return (
    <>
      <Select
        id="cyp-sort-by-menu"
        value={currentSortSlug || sortData?.sortBy?.value || ''}
        onChange={onChange}
        className={classes.sortBy}
        size="small"
        variant="outlined"
      >
        {viewModel &&
          [...viewModel.viewfieldSet.edges]
            .sort((a, b) => a.node.sequence - b.node.sequence)
            .map((field) => (
              <MenuItem
                data-sort-cyp={field.node.fieldLabel}
                value={field.node.field.slug}
                sx={{ color: 'text.primary', backgroundColor: 'background.paper' }}
                key={field.node.id}
              >
                {field.node.fieldLabel}
              </MenuItem>
            ))}
      </Select>

      <DownwardUpwardArrows onArrowClick={onArrowClick} enabledArrow={currentSortOrder || sortData.orderBy} />
    </>
  );
}
